/**
 * Turn a cell into something a rider can act on.
 *
 * A hex id and a score are not directions. What you need at a red light is "the bright
 * patch is the block with Pizza Rothschild and Sushi Basel on it" — landmarks you can
 * navigate to without reading a map.
 */

import {
  decodeMask,
  getSlot,
  MINUTES_PER_SLOT,
  type Venue,
  type WeekMoment,
} from '@tlv/shared';
import { openProbability } from './priors.js';
import type { ScoringContext } from './score.js';

export interface CellDescription {
  /** Closest open venues, nearest first — the landmarks for this cell. */
  landmarks: Array<{ name: string; seconds: number; category: string }>;
  /** How many venues are modelled as open and reachable from here. */
  openVenues: number;
  /** Share of those venues whose hours are guessed rather than known, 0..1. */
  guessedShare: number;
}

const EMPTY: CellDescription = { landmarks: [], openVenues: 0, guessedShare: 0 };

export function describeCell(
  context: ScoringContext,
  h3: string,
  moment: WeekMoment,
  landmarkCount = 4,
): CellDescription {
  const cell = context.bundle.reach.find((entry) => entry.h3 === h3);
  if (!cell) return EMPTY;

  const slot = Math.floor(moment.minutesOfDay / MINUTES_PER_SLOT);

  const candidates: Array<{ venue: Venue; seconds: number; guessed: boolean }> = [];

  for (let i = 0; i < cell.venueIdx.length; i++) {
    const venue = context.bundle.venues[cell.venueIdx[i]!];
    if (!venue) continue;

    let open: boolean;
    let guessed = false;

    if (venue.hours === null) {
      // Count a guessed venue as open only when the category prior is convincing —
      // otherwise a coin-flip venue would be presented as a confident landmark.
      open = openProbability(context.priors, venue.category, moment.day, slot) > 0.5;
      guessed = true;
    } else {
      open = getSlot(decodeMask(venue.hours), moment.day, slot);
    }

    if (open) candidates.push({ venue, seconds: cell.seconds[i]!, guessed });
  }

  candidates.sort((a, b) => a.seconds - b.seconds);

  const guessedCount = candidates.filter((c) => c.guessed).length;

  return {
    landmarks: candidates.slice(0, landmarkCount).map((c) => ({
      name: c.venue.name,
      seconds: c.seconds,
      category: c.venue.category,
    })),
    openVenues: candidates.length,
    guessedShare: candidates.length > 0 ? guessedCount / candidates.length : 0,
  };
}
